import React, { useState, useRef } from "react";
import {
  Bot,
  User,
  AlertCircle,
  Eye,
  EyeOff,
  Download,
  Copy,
  BarChart3,
  LineChart,
  PieChart,
  ExternalLink,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  PieChart as RechartsPieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Pie,
} from "recharts";
import ImageLightbox from "./ImageLightbox";
import PDFViewer from "./PDFViewer";
import FeedbackComponent from "./FeedbackComponent";
import AllUrlsModal from "./AllUrlsModal";
import AllImagesModal from "./AllImagesModal";

const MessageComponent = React.memo(
  ({ message }) => {
    const [showData, setShowData] = useState(false);
    const [showSQL, setShowSQL] = useState(false);
    const [showChartData, setShowChartData] = useState({}); // New state for individual chart data
    const contentRef = useRef(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
    const [pdfViewerIndex, setPdfViewerIndex] = useState(0);

    const [showAllUrls, setShowAllUrls] = useState(false);
    const [showAllImages, setShowAllImages] = useState(false);

    // Utility functions
    const exportToCSV = (data) => {
      if (!data || data.length === 0) return;
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(","),
        ...data.map((row) =>
          headers.map((header) => JSON.stringify(row[header] || "")).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sandvik_data_${new Date().getTime()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    };

    const copyToClipboard = (text) => {
      navigator.clipboard.writeText(text).then(() => {
        console.log("Copied to clipboard");
      });
    };

    const normalizeTableMultilines = (raw) => {
      try {
        const lines = (raw || "").split(/\r?\n/);
        const out = [];
        let i = 0;
        const isSep = (s) =>
          /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(s);

        while (i < lines.length) {
          const line = lines[i];
          const next = lines[i + 1] ?? "";
          const isHeader = line.includes("|") && isSep(next);

          if (!isHeader) {
            out.push(line);
            i++;
            continue;
          }

          // Emit header + separator
          out.push(line);
          out.push(next);
          i += 2;

          // Consume the body
          while (i < lines.length) {
            const l = lines[i];

            // Blank line terminates the table block
            if (/^\s*$/.test(l)) {
              out.push("");
              i++;
              break;
            }

            // Allow additional separator rows (edge cases)
            if (isSep(l)) {
              out.push(l);
              i++;
              continue;
            }

            if (l.includes("|")) {
              // New table row
              let row = l;
              i++;

              // Fold continuation lines (no pipe) into current row
              while (i < lines.length) {
                const cont = lines[i];
                if (/^\s*$/.test(cont) || cont.includes("|") || isSep(cont))
                  break;
                row += " \n" + cont.trim();
                i++;
              }

              out.push(row);
              continue;
            }

            // Non-pipe, non-blank line ends the table block (do not consume it here)
            break;
          }
        }

        return out.join("\n");
      } catch {
        return raw || "";
      }
    };

    // Helper function to extract URLs and images from different sources
    const getUrls = () => {
      // Priority order: direct message props, then nested in search_results, then metadata
      return (
        message.urls ||
        message.search_results?.urls ||
        message.metadata?.search_results?.urls ||
        []
      );
    };

    const getImages = () => {
      // Priority order: direct message props, then nested in search_results, then metadata
      return (
        message.images ||
        message.search_results?.images ||
        message.metadata?.search_results?.images ||
        []
      );
    };

    // Helper function to render content with clickable citations
    const renderContentWithCitations = (content) => {
      if (!content) return null;
      
      const urls = getUrls();
      if (!urls || urls.length === 0) {
        // No URLs available, render normally without citation processing
        return (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={getMarkdownComponents()}
          >
            {normalizeTableMultilines(content)
              .replace(/\n/g, "  \n")
              .replace(/(?<!\n)\s*(\[[^\]]+\]\(#[^)]+\))/g, "\n$1")
              .replace(/(\[[^\]]+\]\(#[^)]+\))\s*[，,;；:：.。]/g, "$1")
              .replace(/(<a\b[^>]*href\s*=\s*["']#[^"']+["'][^>]*>[^<]*<\/a>)(?:\s|&nbsp;)*[，,;；:：.。]/gi, "$1")}
          </ReactMarkdown>
        );
      }
      
      // Split content by citation pattern [1], [2], [1][2], etc.
      const citationPattern = /(\[\d+\](?:\[\d+\])*)/g;
      const parts = content.split(citationPattern);
      
      return parts.map((part, index) => {
        // Check if this part is a citation
        const isCitation = citationPattern.test(part);
        
        if (isCitation) {
          // Extract all numbers from consecutive citations like [1][2][3]
          const numbers = part.match(/\d+/g).map(n => parseInt(n) - 1);
          
          return (
            <span key={index} className="inline-flex items-center">
              {numbers.map((num, i) => {
                // Get the filename for the tooltip
                const url = urls[num];
                const urlTitle = typeof url === "string" ? url : (url?.title || url?.url || '');
                const filename = urlTitle.split('/').pop() || `Document ${num + 1}`;
                
                return (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (num < urls.length) {
                        setPdfViewerIndex(num);
                        setPdfViewerOpen(true);
                      }
                    }}
                    className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 hover:border-blue-300 transition-colors cursor-pointer mx-0.5"
                    title={`View document ${num + 1}: ${filename}`}
                  >
                    {num + 1}
                  </button>
                );
              })}
            </span>
          );
        }
        
        // Regular text - render with markdown
        return (
          <span key={index}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={getMarkdownComponents()}
            >
              {normalizeTableMultilines(part)
                .replace(/\n/g, "  \n")
                .replace(/(?<!\n)\s*(\[[^\]]+\]\(#[^)]+\))/g, "\n$1")
                .replace(/(\[[^\]]+\]\(#[^)]+\))\s*[，,;；:：.。]/g, "$1")
                .replace(/(<a\b[^>]*href\s*=\s*["']#[^"']+["'][^>]*>[^<]*<\/a>)(?:\s|&nbsp;)*[，,;；:：.。]/gi, "$1")}
            </ReactMarkdown>
          </span>
        );
      });
    };

    // Markdown component overrides
    const getMarkdownComponents = () => ({
      h1: ({ node, ...props }) => (
        <h1
          className="text-lg mb-2 mt-4 first:mt-0 text-left"
          {...props}
        />
      ),
      h2: ({ node, ...props }) => (
        <h2
          className="text-base mb-2 mt-3 first:mt-0 text-left"
          {...props}
        />
      ),
      h3: ({ node, ...props }) => (
        <h3
          className="text-sm mb-1 mt-2 first:mt-0 text-left"
          {...props}
        />
      ),
      h4: ({ node, ...props }) => (
        <h4
          className="text-sm mb-1 mt-2 first:mt-0 text-left"
          {...props}
        />
      ),
      h5: ({ node, ...props }) => (
        <h5
          className="text-sm mb-1 mt-2 first:mt-0 text-left"
          {...props}
        />
      ),
      h6: ({ node, ...props }) => (
        <h6
          className="text-sm mb-1 mt-2 first:mt-0 text-left"
          {...props}
        />
      ),
      p: ({ node, ...props }) => (
        <p className="mb-2 last:mb-0 text-left" {...props} />
      ),
      div: ({ node, ...props }) => (
        <div className="text-left" {...props} />
      ),
      ul: ({ node, ...props }) => (
        <ul className="text-left" {...props} />
      ),
      ol: ({ node, ...props }) => (
        <ol className="text-left" {...props} />
      ),
      li: ({ node, ...props }) => (
        <li className="text-left" {...props} />
      ),
      table: ({ node, ...props }) => (
        <div className="overflow-x-auto mb-2">
          <table
            className="min-w-full border border-gray-300 text-xs text-left"
            {...props}
          />
        </div>
      ),
      th: ({ node, ...props }) => (
        <th
          className="border border-gray-300 px-2 py-1 bg-gray-50 text-left"
          {...props}
        />
      ),
      td: ({ node, ...props }) => (
        <td
          className="border border-gray-300 px-2 py-1 text-left"
          {...props}
        />
      ),
      br: () => <br style={{ margin: "1em 0" }} />,
    });

    // InlineChart component
    const InlineChart = ({ config, data }) => {
      if (!config || !data || data.length === 0) return null;

      const colors = [
        "#6F6F6F", // Gray-500
        "#101010", // Black (primary)
        "#A08C64", // Amber (warning accent)
        "#DADADA", // Gray-300
        "#F8F8F8", // Gray-100
      ];

      const gridColor = "#DADADA"; // Gray-300 for grid lines
      const axisColor = "#6F6F6F"; // Neutral n5 for axis
      const tooltipBg = "#fff"; // White tooltip
      const tooltipBorder = "#101010"; // Carbon Black border

      const renderChart = () => {
        switch (config.type?.toLowerCase()) {
          case "bar":
            return (
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                <XAxis
                  dataKey={config.x_axis}
                  fontSize={12}
                  stroke={axisColor}
                  angle={data.length > 10 ? -45 : 0}
                  textAnchor={data.length > 10 ? "end" : "middle"}
                  height={data.length > 10 ? 80 : 60}
                />
                <YAxis fontSize={12} stroke={axisColor} />
                <Tooltip
                  contentStyle={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: 0,
                    color: "#101010",
                  }}
                  itemStyle={{ color: "#1441F5" }}
                  labelStyle={{ color: "#6F6F6F" }}
                />
                <Bar
                  dataKey={config.y_axis}
                  fill={colors[1]}
                  radius={[0, 0, 0, 0]} // sharp corners
                  barSize={28}
                />
              </BarChart>
            );
          case "line":
            return (
              <RechartsLineChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                <XAxis
                  dataKey={config.x_axis}
                  fontSize={12}
                  stroke={axisColor}
                />
                <YAxis fontSize={12} stroke={axisColor} />
                <Tooltip
                  contentStyle={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: 0,
                    color: "#101010",
                  }}
                  itemStyle={{ color: "#1441F5" }}
                  labelStyle={{ color: "#6F6F6F" }}
                />
                <Line
                  type="monotone"
                  dataKey={config.y_axis}
                  stroke={colors[1]}
                  strokeWidth={3}
                  dot={{ stroke: "#101010", strokeWidth: 2, fill: "#fff" }}
                  activeDot={{
                    r: 8,
                    fill: "#1441F5",
                    stroke: "#101010",
                    strokeWidth: 2,
                  }}
                />
              </RechartsLineChart>
            );
          case "pie":
            return (
              <RechartsPieChart>
                <Pie
                  data={data}
                  dataKey={config.y_axis}
                  nameKey={config.x_axis}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill={colors[1]}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toLocaleString('sv-SE', { maximumFractionDigits: 0 })}%`
                  }
                  stroke="#101010"
                  strokeWidth={1}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colors[index % colors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: 0,
                    color: "#101010",
                  }}
                  itemStyle={{ color: "#1441F5" }}
                  labelStyle={{ color: "#6F6F6F" }}
                />
              </RechartsPieChart>
            );
          default:
            return (
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                <XAxis
                  dataKey={config.x_axis}
                  fontSize={12}
                  stroke={axisColor}
                />
                <YAxis fontSize={12} stroke={axisColor} />
                <Tooltip
                  contentStyle={{
                    background: tooltipBg,
                    border: `1px solid ${tooltipBorder}`,
                    borderRadius: 0,
                    color: "#101010",
                  }}
                  itemStyle={{ color: "#1441F5" }}
                  labelStyle={{ color: "#6F6F6F" }}
                />
                <Bar
                  dataKey={config.y_axis}
                  fill={colors[1]}
                  radius={[0, 0, 0, 0]}
                  barSize={28}
                />
              </BarChart>
            );
        }
      };

      return (
        <div className="my-4 p-4 bg-gray-50 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm text-gray-700">
              {config.title || "Data Visualization"}
            </h4>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              {config.type === "bar" && <BarChart3 size={14} />}
              {config.type === "line" && <LineChart size={14} />}
              {config.type === "pie" && <PieChart size={14} />}
              <span className="capitalize">{config.type || "bar"} Chart</span>
            </div>
          </div>
          <div className="w-full h-64 mb-2">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-gray-500 text-center">
            Showing {Math.min(data.length, 20)} of {data.length} records
          </div>
        </div>
      );
    };

    const isUser = message.role === "user";
    const normalizeChartData = (config, fallbackRows) => {
      const rows = Array.isArray(config.data) ? config.data : fallbackRows;
      if (!Array.isArray(rows)) return [];
      return rows.filter(
        (row) =>
          row &&
          row[config.x_axis] !== undefined &&
          row[config.y_axis] !== undefined
      );
    };

    const chartConfigsToRender = (
      message.chart_configs ??
      message.metadata?.chart_configs ??
      (message.chartConfigs && message.chartConfigs.length
        ? message.chartConfigs
        : undefined) ??
      (message.chartConfig ? [message.chartConfig] : [])
    )
      .map((config) => ({
        ...config,
        data: normalizeChartData(config, message.queryData),
      }))
      .filter((config) => Array.isArray(config.data) && config.data.length > 0);

    // Toggle individual chart data visibility
    const toggleChartData = (chartId) => {
      setShowChartData(prev => ({
        ...prev,
        [chartId]: !prev[chartId]
      }));
    };

    return (
      <div
        className={`flex ${
          message.type === "user" ? "justify-end" : "justify-start"
        }`}
        data-message-id={message.id}
      >
        <div
          className={`flex max-w-5xl ${
            message.type === "user" ? "flex-row-reverse" : "flex-row"
          } space-x-2`}
        >
          {/* Avatar */}
          <div
            className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${
              message.type === "user"
                ? "bg-electric-blue ml-2"
                : message.isError
                ? "bg-red-500 mr-2"
                : "bg-white border border-gray-300 mr-2"
            }`}
          >
            {message.type === "user" ? (
              <User className="h-4 w-4 text-white" />
            ) : message.isError ? (
              <AlertCircle className="h-4 w-4 text-white" />
            ) : (
              <Bot className="h-4 w-4 text-black" />
            )}
          </div>

          {/* Message Bubble */}
          <div
            className={`px-4 py-3 ${
              message.type === "user"
                ? "bg-electric-blue text-white"
                : message.isError
                ? "bg-red-50 border border-red-200 text-red-800"
                : "bg-white border border-gray-200 text-black"
            }`}
          >
            {message.type === "user" ? (
              <p className="text-sm whitespace-pre-wrap leading-relaxed">
                {message.content}
              </p>
            ) : (
              <div>
                {/* System Message */}
                {message.systemMessage && (
                  <div className="mb-2 flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1">
                    <div className="flex space-x-1 mr-2">
                      <div className="w-1 h-1 bg-blue-600 animate-pulse"></div>
                      <div
                        className="w-1 h-1 bg-blue-600 animate-pulse"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <div
                        className="w-1 h-1 bg-blue-600 animate-pulse"
                        style={{ animationDelay: "0.4s" }}
                      ></div>
                    </div>
                    {message.systemMessage}
                  </div>
                )}

                {/* Filters */}
                {message.metadata?.filters &&
                  Object.keys(message.metadata.filters).length > 0 && (
                    <div className="mb-2 text-xs text-gray-500">
                      <strong>Filters Used:</strong>
                      <ul className="list-disc list-inside">
                        {Object.entries(message.metadata.filters).map(
                          ([key, value]) => (
                            <li key={key}>
                              <span className="capitalize">
                                {key.replace(/_/g, " ")}:
                              </span>{" "}
                              {value}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                {/* Content with Citations */}
                {message.isStreaming ? (
                  <div className="text-sm leading-relaxed prose prose-sm max-w-none text-left [&>*]:text-left">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={getMarkdownComponents()}
                    >
                      {normalizeTableMultilines(message.content)
                        .replace(/\n/g, "  \n")
                        .replace(/(?<!\n)\s*(\[[^\]]+\]\(#[^)]+\))/g, "\n$1")
                        .replace(
                          /(\[[^\]]+\]\(#[^)]+\))\s*[，,;；:：.。]/g,
                          "$1"
                        )
                        .replace(
                          /(<a\b[^>]*href\s*=\s*["']#[^"']+["'][^>]*>[^<]*<\/a>)(?:\s|&nbsp;)*[，,;；:：.。]/gi,
                          "$1"
                        )}
                    </ReactMarkdown>
                    <span className="inline-block w-0.5 h-4 bg-gray-600 animate-pulse ml-0.5 align-middle"></span>
                  </div>
                ) : (
                  <div className="text-sm leading-relaxed prose prose-sm max-w-none text-left [&>*]:text-left">
                    {renderContentWithCitations(message.content)}
                  </div>
                )}

                {/* URLs Section */}
                {getUrls().length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm text-gray-700 mb-2">
                      Source URLs ({getUrls().length})
                    </h4>
                    <div className="space-y-2 text-left">
                      {getUrls()
                        .slice(0, 10)
                        .map((url, idx) => {
                          const urlString = typeof url === "string" ? url : url.url;
                          const urlTitle = typeof url === "string" ? url : url.title || url.url;
                          
                          // Extract just the filename (last part after final slash)
                          const filename = urlTitle.split('/').pop();
                          
                          return (
                            <div
                              key={idx}
                              className={`p-3 bg-gray-50 rounded border border-gray-200 text-sm break-words cursor-pointer hover:bg-gray-100 transition-colors`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPdfViewerIndex(idx);
                                setPdfViewerOpen(true);
                              }}
                            >
                              <span className="text-black hover:underline">
                                {filename}
                              </span>
                              {url.document_type && (
                                <span className="ml-2 text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                  {url.document_type}
                                </span>
                              )}
                              <span className="ml-2 text-xs text-blue-600">
                                Click to preview
                              </span>
                            </div>
                          );
                        })}
                    </div>

                    {/* Show "View all" button if more than 10 URLs */}
                    {getUrls().length > 10 && (
                      <div className="mt-3 text-center">
                        <button
                          onClick={() => setShowAllUrls(true)}
                          className="text-sm bg-gray-600 text-white px-4 py-2 hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                        >
                          <ExternalLink size={14} />
                          View all {getUrls().length} URLs
                        </button>
                      </div>
                    )}

                    {/* All URLs Modal */}
                    {showAllUrls && (
                      <AllUrlsModal
                        urls={getUrls()}
                        onClose={() => setShowAllUrls(false)}
                        onUrlSelect={(index) => {
                          setPdfViewerIndex(index);
                          setPdfViewerOpen(true);
                          setShowAllUrls(false);
                        }}
                      />
                    )}

                    {/* PDF Viewer */}
                    {pdfViewerOpen && (
                      <PDFViewer
                        pdfs={getUrls()}
                        initialIndex={pdfViewerIndex}
                        onClose={() => setPdfViewerOpen(false)}
                      />
                    )}
                  </div>
                )}

                {/* Images Section */}
                {getImages().length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm text-gray-700 mb-2">
                      Related Images ({getImages().length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {getImages()
                        .slice(0, 10)
                        .map((image, idx) => (
                          <div
                            key={idx}
                            className="border border-gray-200 p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => {
                              setLightboxIndex(idx);
                              setLightboxOpen(true);
                            }}
                          >
                            <img
                              src={image.url}
                              alt={`Related image ${idx + 1}`}
                              className="w-full h-48 object-contain bg-white mb-2"
                              loading="lazy"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "block";
                              }}
                            />
                            <div
                              style={{ display: "none" }}
                              className="w-full h-48 bg-gray-200 mb-2 flex items-center justify-center text-gray-500 text-sm"
                            >
                              Image failed to load
                            </div>
                            {image.document && (
                              <p className="text-xs text-gray-600 break-words">
                                <strong>Source:</strong>{" "}
                                {image.document.split("/").pop()}
                              </p>
                            )}
                            {image.document_type && (
                              <span className="inline-block text-xs text-gray-500 bg-gray-200 px-2 py-1 mt-1">
                                {image.document_type}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>

                    {/* Show "View all" button if more than 10 images */}
                    {getImages().length > 10 && (
                      <div className="mt-3 text-center">
                        <button
                          onClick={() => setShowAllImages(true)}
                          className="text-sm bg-gray-600 text-white px-4 py-2 hover:bg-gray-700 transition-colors inline-flex items-center gap-2"
                        >
                          <ExternalLink size={14} />
                          View all {getImages().length} images
                        </button>
                      </div>
                    )}

                    {/* All Images Modal */}
                    {showAllImages && (
                      <AllImagesModal
                        images={getImages()}
                        onClose={() => setShowAllImages(false)}
                        onImageSelect={(index) => {
                          setLightboxIndex(index);
                          setLightboxOpen(true);
                          setShowAllImages(false);
                        }}
                      />
                    )}

                    {/* Lightbox */}
                    {lightboxOpen && (
                      <ImageLightbox
                        images={getImages()}
                        initialIndex={lightboxIndex}
                        onClose={() => setLightboxOpen(false)}
                      />
                    )}
                  </div>
                )}

                {/* Chart rendering */}
                {chartConfigsToRender.length > 0 && (
                  <div className="space-y-4 mt-4">
                    {chartConfigsToRender.map((config) => (
                      <div key={config.id ?? config.title} className="border border-gray-200 rounded">
                        {/* Chart */}
                        <InlineChart
                          config={config}
                          data={config.data}
                        />
                        
                        {/* Chart Controls */}
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                          <div className="text-xs text-gray-600">
                            <span className="">{config.data.length}</span> data points
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleChartData(config.id ?? config.title)}
                              className="text-xs bg-gray-600 text-white px-3 py-1 hover:bg-gray-700 flex items-center gap-1 rounded"
                            >
                              {showChartData[config.id ?? config.title] ? (
                                <>
                                  <EyeOff size={10} />
                                  Hide Data
                                </>
                              ) : (
                                <>
                                  <Eye size={10} />
                                  View Data
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => exportToCSV(config.data)}
                              className="text-xs bg-gray-600 text-white px-3 py-1 hover:bg-gray-700 flex items-center gap-1 rounded"
                            >
                              <Download size={10} />
                              CSV
                            </button>
                          </div>
                        </div>

                        {/* Chart Data Table - Collapsible */}
                        {showChartData[config.id ?? config.title] && (
                          <div className="border-t border-gray-200">
                            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                              <h5 className="text-sm text-gray-800 flex items-center gap-2">
                                <BarChart3 size={14} />
                                Data for "{config.title}"
                              </h5>
                              <div className="text-xs text-gray-600 mt-1">
                                <span className="">X-axis:</span> {config.x_axis} • 
                                <span className=" ml-2">Y-axis:</span> {config.y_axis}
                              </div>
                            </div>
                            
                            <div className="max-h-64 overflow-auto">
                              <table className="w-full text-xs border-collapse">
                                <thead className="bg-gray-100 sticky top-0">
                                  <tr>
                                    <th className="border border-gray-200 px-3 py-2 text-left text-gray-700">
                                      #
                                    </th>
                                    {config.data.length > 0 && Object.keys(config.data[0]).map((key) => (
                                      <th key={key} className="border border-gray-200 px-3 py-2 text-left text-gray-700">
                                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {config.data.map((row, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                      <td className="border border-gray-200 px-3 py-2 text-gray-500">
                                        {idx + 1}
                                      </td>
                                      {Object.values(row).map((value, i) => (
                                        <td key={i} className="border border-gray-200 px-3 py-2">
                                          <span className="block truncate max-w-xs" title={value}>
                                            {typeof value === 'number' ? value.toLocaleString('sv-SE') : value}
                                          </span>
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            
                            <div className="bg-gray-50 px-4 py-2 flex justify-between items-center text-xs text-gray-600">
                              <span>📊 {config.data.length} total records</span>
                              <button
                                onClick={() => copyToClipboard(JSON.stringify(config.data, null, 2))}
                                className="bg-gray-600 text-white px-2 py-1 hover:bg-gray-700 flex items-center gap-1 rounded"
                              >
                                <Copy size={10} />
                                Copy JSON
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Enhanced features - only after streaming completes */}
                {!message.isStreaming &&
                  message.type === "bot" &&
                  !message.isError && (
                    <div className="mt-4 space-y-2">
                      {/* Metadata */}
                      {(message.intent ||
                        message.recordCount !== undefined) && (
                        <div className="text-xs text-gray-500 border-t pt-2 flex flex-wrap gap-2">
                          {message.intent && (
                            <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1">
                              Intent: {message.intent}
                            </span>
                          )}
                          {message.recordCount !== undefined && (
                            <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1">
                              {message.recordCount} records
                            </span>
                          )}
                          {message.responseType && (
                            <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1">
                              Type: {message.responseType}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2">
                        {message.queryData && (
                          <button
                            onClick={() => setShowData(!showData)}
                            className="text-xs bg-electric-blue text-white px-3 py-1 hover:bg-electric-blue-eb3 flex items-center gap-1"
                          >
                            {showData ? (
                              <EyeOff size={12} />
                            ) : (
                              <Eye size={12} />
                            )}
                            {showData ? "Hide Data" : "View Data"}
                          </button>
                        )}

                        {(message.sqlQuery ||
                          (message.sqlQueries &&
                            message.sqlQueries.length > 0)) && (
                          <button
                            onClick={() => setShowSQL(!showSQL)}
                            className="text-xs bg-electric-blue text-white px-3 py-1 hover:bg-electric-blue-eb3 flex items-center gap-1"
                          >
                            {showSQL ? <EyeOff size={12} /> : <Eye size={12} />}
                            {showSQL
                              ? "Hide SQL"
                              : `View SQL${
                                  message.sqlQueries
                                    ? ` (${message.sqlQueries.length})`
                                    : ""
                                }`}
                          </button>
                        )}

                        {message.queryData && (
                          <button
                            onClick={() => exportToCSV(message.queryData)}
                            className="text-xs bg-electric-blue text-white px-3 py-1 hover:bg-electric-blue-eb3 flex items-center gap-1"
                          >
                            <Download size={12} />
                            Export CSV
                          </button>
                        )}
                      </div>

                      {/* Data table */}
                      {showData && message.queryData && (
                        <div className="mt-3 border-t pt-3">
                          <div className="max-h-60 overflow-auto">
                            <table className="w-full text-xs border border-gray-300 table-fixed">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  {Object.keys(message.queryData[0] || {}).map(
                                    (key) => (
                                      <th
                                        key={key}
                                        className="border border-gray-300 px-2 py-1 text-left truncate"
                                        style={{
                                          minWidth: "100px",
                                          maxWidth: "200px",
                                        }}
                                      >
                                        {key}
                                      </th>
                                    )
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {message.queryData
                                  .slice(0, 10)
                                  .map((row, idx) => (
                                    <tr
                                      key={idx}
                                      className={
                                        idx % 2 === 0
                                          ? "bg-white"
                                          : "bg-gray-50"
                                      }
                                    >
                                      {Object.values(row).map((value, i) => (
                                        <td
                                          key={i}
                                          className="border border-gray-300 px-2 py-1 truncate"
                                          title={value} // Show full value on hover
                                        >
                                          {value}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                            {message.queryData.length > 10 && (
                              <p className="text-xs text-gray-500 mt-2">
                                Showing first 10 of {message.queryData.length}{" "}
                                records
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Multiple SQL Queries */}
                      {showSQL &&
                        message.sqlQueries &&
                        message.sqlQueries.length > 0 && (
                          <div className="mt-3 border-t pt-3">
                            <h4 className="text-sm text-gray-700 mb-2">
                              SQL Queries ({message.sqlQueries.length})
                            </h4>
                            <div className="space-y-3">
                              {message.sqlQueries.map((query, index) => (
                                <div
                                  key={index}
                                  className="border border-gray-300 rounded"
                                >
                                  <div className="bg-gray-50 px-3 py-2 text-xs text-gray-800 border-b border-gray-300 flex items-center justify-between">
                                    <span>Query {index + 1}</span>
                                    <button
                                      onClick={() => copyToClipboard(query)}
                                      className="text-xs bg-electric-blue text-white px-2 py-1 hover:bg-electric-blue-eb3 flex items-center gap-1 rounded"
                                      title="Copy this query"
                                    >
                                      <Copy size={10} />
                                      Copy
                                    </button>
                                  </div>
                                  <div className="bg-gray-900 p-3 font-mono text-xs overflow-x-auto">
                                    <pre className="whitespace-pre-wrap break-words text-green-400">
                                      {query}
                                    </pre>
                                  </div>
                                </div>
                              ))}

                              {/* Copy All Queries Button */}
                              {message.sqlQueries.length > 1 && (
                                <div className="mt-3 flex justify-center">
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        message.sqlQueries.join(
                                          "\n\n-- Next Query --\n\n"
                                        )
                                      )
                                    }
                                    className="text-xs bg-gray-700 text-white px-4 py-2 hover:bg-electric-blue-eb3 flex items-center gap-2 rounded"
                                  >
                                    <Copy size={12} />
                                    Copy All {message.sqlQueries.length} Queries
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      {/* Single SQL Query (fallback for backward compatibility) */}
                      {showSQL && message.sqlQuery && !message.sqlQueries && (
                        <div className="mt-3 border-t pt-3">
                          <div className="border border-gray-300 rounded overflow-hidden">
                            <div className="bg-gray-50 px-3 py-2 text-xs text-gray-800 border-b border-gray-300">
                              SQL Query
                            </div>
                            <div className="bg-gray-900 p-3 font-mono text-xs overflow-x-auto">
                              <pre className="whitespace-pre-wrap break-words text-green-400">
                                {message.sqlQuery}
                              </pre>
                            </div>
                          </div>
                          <button
                            onClick={() => copyToClipboard(message.sqlQuery)}
                            className="text-xs bg-electric-blue text-white px-3 py-1 hover:bg-electric-blue-eb3 mt-2 flex items-center gap-1 rounded"
                          >
                            <Copy size={12} />
                            Copy SQL
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {/* Feedback Component */}
                {!message.isStreaming &&
                  !message.isError &&
                  message.type === "bot" &&
                  message.id !== 1 && (
                    <FeedbackComponent
                      messageId={message.id}
                      sessionId={message.sessionId} 
                      initialFeedback={message.feedback}
                    />
                  )}
              </div>
            )}

            <p
              className={`text-xs mt-2 ${
                message.type === "user"
                  ? "text-gray-300"
                  : message.isError
                  ? "text-red-600"
                  : "text-gray-500"
              }`}
            >
              {message.timestamp.toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.message.id === nextProps.message.id &&
      prevProps.message.content === nextProps.message.content &&
      prevProps.message.isStreaming === nextProps.message.isStreaming &&
      prevProps.message.systemMessage === nextProps.message.systemMessage &&
      JSON.stringify(prevProps.message.urls || []) ===
        JSON.stringify(nextProps.message.urls || []) &&
      JSON.stringify(prevProps.message.images || []) ===
        JSON.stringify(nextProps.message.images || []) &&
      JSON.stringify(prevProps.message.metadata || {}) ===
        JSON.stringify(nextProps.message.metadata || {})
    );
  }
);

export default MessageComponent;
